import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { BasicQuestion } from "@repo/app-types/client/client";
import { useReducer, type Dispatch } from "react";
import { v4 as uuidv4 } from "uuid";

export interface QuestionsState {
  questions: readonly QuestionWithTemporaryId[];
  hasMadeChanges: boolean;
}

export type QuestionsAction = ReturnType<
  (typeof questionsActions)[keyof typeof questionsActions]
>;

export type QuestionsDispatch = Dispatch<QuestionsAction>;

export interface QuestionWithTemporaryId extends BasicQuestion {
  id: string;
}

/**
 * For stable react keys, we append random IDs to questions.
 */
function appendTemporaryIdToQuestions(
  questions: readonly BasicQuestion[],
): QuestionWithTemporaryId[] {
  return questions.map((q) => ({
    ...q,
    id: uuidv4(),
  }));
}

function createEmptyQuestion(): QuestionWithTemporaryId {
  return {
    question: "",
    id: uuidv4(),
  };
}

const initialState: QuestionsState = {
  questions: [],
  hasMadeChanges: false,
};

// Only used with `useReducer` below.
const slice = createSlice({
  name: "questions",
  initialState,
  reducers: {
    initialized: (state, action: PayloadAction<readonly BasicQuestion[]>) => {
      const basicQuestions = action.payload;

      state.questions =
        basicQuestions.length > 0
          ? appendTemporaryIdToQuestions(basicQuestions)
          : [createEmptyQuestion()];
      state.hasMadeChanges = false;
    },

    imported: (state, action: PayloadAction<readonly BasicQuestion[]>) => {
      state.questions = appendTemporaryIdToQuestions(action.payload);
      state.hasMadeChanges = true;
    },

    reordered: (
      state,
      action: PayloadAction<readonly QuestionWithTemporaryId[]>,
    ) => {
      state.questions = [...action.payload];
      state.hasMadeChanges = true;
    },

    added: (state) => {
      state.questions.push(createEmptyQuestion());
      state.hasMadeChanges = true;
    },

    inserted: (state, action: PayloadAction<{ index: number }>) => {
      state.questions.splice(action.payload.index, 0, createEmptyQuestion());
      state.hasMadeChanges = true;
    },

    updated: (
      state,
      action: PayloadAction<{ index: number; basicQuestion: BasicQuestion }>,
    ) => {
      const { index, basicQuestion } = action.payload;

      const existingQuestion = state.questions[index];
      if (existingQuestion) {
        state.questions[index] = {
          ...basicQuestion,
          id: existingQuestion.id,
        };
      }
      state.hasMadeChanges = true;
    },

    deleted: (state, action: PayloadAction<{ index: number }>) => {
      state.questions = state.questions.filter(
        (_, i) => i !== action.payload.index,
      );
      state.hasMadeChanges = true;
    },

    deletedAll: (state) => {
      state.questions = [];
      state.hasMadeChanges = true;
    },
  },
});

export const questionsActions = slice.actions;

export function useQuestionsStore(
  initialBasicQuestions: readonly BasicQuestion[],
) {
  return useReducer(slice.reducer, initialBasicQuestions, (basicQuestions) =>
    slice.reducer(undefined, questionsActions.initialized(basicQuestions)),
  );
}
